import { customFetcher } from "./http";
import { getAuthorizedRequestConfig, getAuthorizedRequestConfigWithWallet } from "./auth";

export type PhoneNumberStatus = "PENDING" | "ACTIVE" | "REVOKED";

export type PhoneNumberResponse = {
  id: string;
  msisdn: string;
  walletId: string;
  userId: string;
  nftId: string | null;
  balance: string;
  currency: string;
  callDst: string;
  smsDst: string;
  status: PhoneNumberStatus;
  activatedAt: string | null;
  lastBalanceSyncAt: string | null;
  auctionEndTime: string | null;
  esimProfile: {
    id: string;
    iccid: string;
    activationCode: string;
    smdpPlus: string;
    status: string;
  } | null;
  nft?: {
    id: string;
    itemAddress: string;
  } | null;
  wallet?: {
    id: string;
    friendlyAddress: string;
  } | null;
  createdAt: string;
  updatedAt: string;
};


const getConfigWithWallet = (walletId: string | undefined) => {
  if (!walletId) {
    throw new Error('Wallet ID is required for phone number operations');
  }
  return getAuthorizedRequestConfigWithWallet(() => walletId);
};

export const listPhoneNumbers = async (params: { walletId: string; userId?: string; syncBalances?: boolean }) =>
  customFetcher<PhoneNumberResponse[]>({
    url: "/phone-numbers",
    params: {
      ...(params?.syncBalances ? { syncBalances: 'true' } : {}),
    },
  }, getConfigWithWallet(params.walletId));

export const generatePhoneNumber = async (walletId: string) =>
  customFetcher<PhoneNumberResponse>(
    {
      url: "/phone-numbers/generate",
      method: "POST",
    },
    getConfigWithWallet(walletId),
  );

export const createPhoneNumberFromNft = async (nftId: string, walletId: string) =>
  customFetcher<PhoneNumberResponse>(
    {
      url: `/phone-numbers/create-from-nft/${nftId}`,
      method: "POST",
    },
    getConfigWithWallet(walletId),
  );

export const getPhoneNumber = async (phoneNumberId: string, walletId: string) =>
  customFetcher<PhoneNumberResponse>(
    `/phone-numbers/${phoneNumberId}`,
    getConfigWithWallet(walletId),
  );

export const activatePhoneNumber = async (phoneNumberId: string, walletId: string) =>
  customFetcher<PhoneNumberResponse>(
    {
      url: `/phone-numbers/${phoneNumberId}/activate`,
      method: "POST",
    },
    getConfigWithWallet(walletId),
  );

export const activateEsim = async (phoneNumberId: string, walletId: string) =>
  customFetcher<PhoneNumberResponse>(
    {
      url: `/phone-numbers/${phoneNumberId}/activate-esim`,
      method: "POST",
    },
    getConfigWithWallet(walletId),
  );

export const reissueEsim = async (phoneNumberId: string, walletId: string) =>
  customFetcher<PhoneNumberResponse>(
    {
      url: `/phone-numbers/${phoneNumberId}/reissue-esim`,
      method: "POST",
    },
    getConfigWithWallet(walletId),
  );

export const revokeEsim = async (phoneNumberId: string, walletId: string) =>
  customFetcher<PhoneNumberResponse>(
    {
      url: `/phone-numbers/${phoneNumberId}/revoke-esim`,
      method: "POST",
    },
    getConfigWithWallet(walletId),
  );

export const syncPhoneNumberBalance = async (
  phoneNumberId: string,
  walletId: string,
  options?: { force?: boolean },
) =>
  customFetcher<PhoneNumberResponse>(
    {
      url: `/phone-numbers/${phoneNumberId}/sync-balance`,
      method: "POST",
      params: options?.force ? { force: 'true' } : undefined,
    },
    getConfigWithWallet(walletId),
  );

export const sendEsimQrToTelegram = async (phoneNumberId: string, walletId: string) =>
  customFetcher<void>(
    {
      url: `/phone-numbers/${phoneNumberId}/send-esim-qr`,
      method: "POST",
    },
    getConfigWithWallet(walletId),
  );

export type TopUpPhoneNumberRequest = {
  amount: number;
  currency?: string;
  description?: string;
  idempotencyKey?: string;
};

export const topUpPhoneNumber = async (
  phoneNumberId: string,
  walletId: string,
  data: TopUpPhoneNumberRequest,
) =>
  customFetcher<PhoneNumberResponse>(
    {
      url: `/phone-numbers/${phoneNumberId}/top-up`,
      method: "POST",
      data,
    },
    getConfigWithWallet(walletId),
  );

// Re-export Telecom API types from centralized location
export type {
  PhoneNumberInfo,
  GetNumbersResponse,
  DataBundle,
  EsimProfile as EsimProfileInfo,
  PhoneProfile,
} from "./telecom-types";

export const getTelecomNumbers = async (walletId: string) =>
  customFetcher<import("./telecom-types").GetNumbersResponse>(
    `/phone-numbers/telecom/numbers`,
    getConfigWithWallet(walletId),
  );

export const getTelecomPhoneProfile = async (msisdn: string, walletId: string) =>
  customFetcher<import("./telecom-types").PhoneProfile>(
    `/phone-numbers/telecom/profile?msisdn=${encodeURIComponent(msisdn)}`,
    getConfigWithWallet(walletId),
  );

